import {
  ChicImg,
  Chick,
  ArtevicImg,
  ElorvaImg,
  MosonImg,
  BrandP1,
  BrandP2,
  BrandP3,
  Prb1,
  Prb2,
  elorvaService1,
  elorvaGal3,
  elorvaService2,
  elorvaGal1,
  elorvaService3,
  elorvaService4,
  elorvaGal2,
  mosonService1,
  mosonService2,
  mosonService3,
  mosonService4,
  mosonGal1,
  mosonGal2,
  mosonGal3,
  artevicService1,
  artevicService4,
  artevicService2,
  artevicService3,
  artevicGal1,
  artevicGal2,
  artevicGal3,
} from "@/assets/images/image";
import { CaseStudyProps } from "@/lib/types";

export const caseStudyData: CaseStudyProps[] = [
  {
    id: "1",
    tag: "UI/UX Design",
    slug: "chic-charming",
    name: "Chic & Charming",
    title: "THE CHIC & CHARMING EXPERIENCE",
    description:
      "A thoughtfully designed fashion marketplace focused on clarity, usability, and seamless mobile experiences.",
    image: ChicImg,
    bgColor: "#F5F3EE",
    metric: "+40%",
    metricLabel: "Conversion rate · −25% bounce in month one",
    link: "#",
    details: {
      aboutTitle: "About",
      aboutDesc:
        "This project reimagined the digital presence of a contemporary fashion marketplace, blending elegance with performance. Our goal was to craft an experience that feels refined yet intuitive — where product discovery flows naturally and every interaction feels effortless. The result is a platform that balances aesthetic sophistication with seamless functionality across devices.",
      bgImage: Chick,
      projectScope: [
        {
          header: "PROBLEM STATEMENT",
          desc: "Despite a strong market reputation, the existing website failed to deliver a seamless customer journey. Users faced friction in product discovery, inconsistent messaging, and limited mobile optimization.",
          image: Prb1,
        },
        {
          header: "RESEARCH & INSIGHTS",
          desc: "Our research revealed that 70% of fashion shoppers prefer a minimalist interface where product imagery takes center stage. We focused on reducing click-depth and enhancing visual hierarchy.",
          image: Prb2,
          images: [Prb2, Prb1],
        },
        {
          header: "TECHNICAL ARCHITECTURE",
          desc: "We deployed a Next.js-based architecture with optimized image delivery for faster global performance. This ensured near-instant page load times across regions, significantly improving user experience. As a result, bounce rates dropped by 25% within the first month, demonstrating the effectiveness of performance-focused optimization and scalable frontend infrastructure.",
        },
        {
          header: "PROJECT TRANSFORMATION",
          desc: "The marketplace has achieved a 40% increase in conversion rates, reflecting strong user engagement and improved buying behavior. Its mobile-first design allows users to shop seamlessly across all devices, ensuring convenience and accessibility anytime, anywhere, while enhancing overall user experience and driving consistent growth in customer interactions and sales performance.",
        },
      ],
      gallery: [BrandP1, BrandP2, BrandP3],
      summaryStatement:
        "THE PLATFORM WAS DESIGNED AS A BOLD EXPRESSION OF THE GROUP’S IDENTITY — WHERE MOVEMENT, TEXTURE, AND TYPOGRAPHY WORK TOGETHER TO CREATE A DISTINCTIVE VISUAL LANGUAGE. EVERY DESIGN DECISION REFLECTS THE COMPANY’S FORWARD-THINKING MINDSET, COLLABORATIVE CULTURE, AND COMMITMENT TO CRAFTSMANSHIP AT A GLOBAL STANDARD.",
    },
    metadata: {
      title: "THE CHIC & CHARMING EXPERIENCE",
      description:
        "A thoughtfully designed fashion marketplace focused on clarity, usability, and seamless mobile experiences.",
      keywords: [
        "Fashion marketplace UX",
        "E-commerce design",
        "Mobile-first shopping",
        "Octarnal Case Study",
      ],
      ogImage: "/openg.jpg",
    },
  },
  {
    id: "6",
    tag: "E-Commerce",
    slug: "elorva-perfumes",
    name: "Elorva Perfumes",
    title: "ELORVA — LUXURY FRAGRANCE",
    description:
      "A premium e-commerce platform for authentic luxury perfumes and designer fragrances in Bangladesh, scaling to BDT 3M+ revenue.",
    image: ElorvaImg,
    bgColor: "#b2d127",
    metric: "BDT 3M+",
    metricLabel: "Revenue scaled in the authentic fragrance category",
    link: "https://www.elorvabd.com",
    details: {
      aboutTitle: "Luxury Redefined",
      aboutDesc:
        "Elorva Perfumes is Bangladesh's trusted luxury fragrance retailer, offering 100% authentic designer scents from Dior, Chanel, Tom Ford, Creed, and exclusive niche houses like Initio Parfums, Xerjoff, and Kilian. We designed and developed a full-stack e-commerce platform that elevated the brand from a small operation to a revenue powerhouse — crossing the BDT 3 million milestone through conversion-focused UX, premium visual storytelling, and strategic digital growth.",
      bgImage: "/elorva-new.png",
      projectScope: [
        {
          header: "BRAND POSITIONING",
          desc: "Elorva needed a digital presence that communicated luxury and authenticity in a market flooded with counterfeit perfumes. We crafted a visual identity using deep green and gold tones — evoking sophistication and trust — paired with cinematic product photography and premium packaging presentation.",
          image: elorvaService4,
        },
        {
          header: "E-COMMERCE ARCHITECTURE",
          desc: "Built on Next.js with server-side rendering for lightning-fast page loads and impeccable SEO. Integrated advanced product filtering by brand, gender, size (decants: 3ml/6ml/15ml and full bottles), gift boxes, and combos. Implemented bKash, Nagad, Rocket, and Cash on Delivery payment gateways for the Bangladeshi market.",
          image: elorvaService1,
          images: [elorvaService2, elorvaService3],
        },
        {
          header: "CONVERSION OPTIMIZATION",
          desc: "Deployed structured FAQ schemas, product-level JSON-LD markup, and geo-targeted SEO content to dominate local search. Facebook Pixel and Google Analytics 4 integration enabled data-driven retargeting that drove repeat purchases and a 3x improvement in returning customer rate.",
        },
        {
          header: "REVENUE MILESTONE",
          desc: "Through strategic UX optimization, trust-building design elements (authenticity guarantees, premium unboxing experience), and a mobile-first approach, we helped Elorva cross the BDT 3 million revenue milestone — transforming a passion project into Bangladesh's go-to destination for luxury fragrances.",
        },
      ],
      gallery: [elorvaGal1, elorvaGal3, elorvaGal2],
      summaryStatement:
        "ELORVA PERFUMES PROVES THAT PREMIUM E-COMMERCE IS NOT JUST ABOUT PRODUCTS — IT'S ABOUT TRUST, EXPERIENCE, AND PRECISION. WE ENGINEERED A PLATFORM WHERE EVERY PIXEL COMMUNICATES AUTHENTICITY AND EVERY INTERACTION BUILDS CONFIDENCE. FROM ZERO TO BDT 3 MILLION IN REVENUE, THIS PROJECT EMBODIES OUR BELIEF THAT GREAT DESIGN DRIVES MEASURABLE BUSINESS IMPACT.",
    },
    metadata: {
      title: "ELORVA — LUXURY FRAGRANCE COMMERCE",
      description:
        "A premium e-commerce platform for authentic luxury perfumes and designer fragrances in Bangladesh, scaling to BDT 3M+ revenue.",
      keywords: [
        "Luxury e-commerce design",
        "Perfume store website",
        "E-commerce Bangladesh",
        "Octarnal Case Study",
      ],
      ogImage: "/elorva.png",
    },
  },
  {
    id: "7",
    tag: "Web Development",
    slug: "moson-media",
    name: "Moson Media",
    title: "MOSON MEDIA — SAAS VIDEO GROWTH ENGINE",
    description:
      "A high-conversion landing page for a SaaS-focused video marketing agency that has powered growth for 45+ startups.",
    image: MosonImg,
    bgColor: "#F4B1A4",
    metric: "45+",
    metricLabel: "Startups powered through this funnel",
    link: "https://mosonmedia.vercel.app",
    details: {
      aboutTitle: "Growth Through Motion",
      aboutDesc:
        "Moson Media is a video marketing agency that turns product features into paying customers for SaaS companies. With 45+ startups already onboard, they needed a digital presence that matched their energy — fast, bold, and conversion-driven. We designed and developed a high-impact landing page that communicates their value proposition within seconds and drives qualified leads directly into their pipeline.",
      bgImage: "/moson-media.png",
      projectScope: [
        {
          header: "STRATEGIC POSITIONING",
          desc: "The SaaS video market is crowded. We positioned Moson Media as the go-to agency for startups that need videos driving real growth — not just polished content. The messaging framework was built around outcomes: signups, conversions, and revenue, not just views and impressions.",
          image: mosonService1,
        },
        {
          header: "PERFORMANCE-FIRST DESIGN",
          desc: "Built as a React single-page application with Vite for blazing-fast load times. The coral (#EB755E) brand palette creates urgency and warmth, while the Outfit typography system ensures readability across all devices. Every section is designed to reduce friction and drive visitors toward the CTA.",
          image: mosonService4,
          images: [mosonService2, mosonService3],
        },
        {
          header: "CONVERSION ARCHITECTURE",
          desc: "Implemented structured data markup, comprehensive meta tags for social sharing, and an SEO-optimized content hierarchy. The page is engineered to rank for SaaS video marketing keywords while converting organic traffic into booked calls.",
        },
        {
          header: "SOCIAL PROOF ENGINE",
          desc: "Strategically placed testimonials, client logos, and project showcases build trust at every scroll point. Video embeds demonstrate the agency's work quality without requiring visitors to leave the page, reducing bounce rates and increasing time on site.",
        },
      ],
      gallery: [mosonGal2, mosonGal1, mosonGal3],
      summaryStatement:
        "MOSON MEDIA'S DIGITAL PRESENCE IS A MASTERCLASS IN CONVERSION-DRIVEN DESIGN. WE STRIPPED AWAY THE NOISE TO CREATE A SINGLE-PAGE EXPERIENCE THAT DOES ONE THING EXCEPTIONALLY WELL — TURNS VISITORS INTO BOOKED CALLS. WITH 45+ SAAS STARTUPS ALREADY IN THEIR PORTFOLIO, THE SITE SERVES AS PROOF THAT FOCUSED DESIGN DELIVERS MEASURABLE GROWTH.",
    },
    metadata: {
      title: "MOSON MEDIA — SAAS VIDEO GROWTH ENGINE",
      description:
        "A high-conversion landing page for a SaaS-focused video marketing agency that has powered growth for 45+ startups.",
      keywords: [
        "SaaS video marketing",
        "Video agency website",
        "Landing page design",
        "Octarnal Case Study",
      ],
      ogImage: "/openg.jpg",
    },
  },
  {
    id: "8",
    tag: "Web Development",
    slug: "artevic-media",
    name: "Artevic Media",
    title: "ARTEVIC MEDIA — CINEMATIC CONTENT STUDIO",
    description:
      "A premium agency website for a video editing studio serving global brands including Daymond John and Biohack Yourself.",
    image: ArtevicImg,
    bgColor: "#FFF7D6",
    metric: "Global",
    metricLabel: "Roster incl. Daymond John and Biohack Yourself",
    link: "https://www.artevic.media",
    details: {
      aboutTitle: "Visual Storytelling",
      aboutDesc:
        "Artevic Media, founded by Nur Alam Shakib, is a video editing and content strategy agency that goes beyond production — they help brands communicate their ideas, values, and vision through meaningful visual storytelling. With clients like Daymond John and Biohack Yourself, Artevic needed a website that reflected their cinematic quality and positioned them as a premium global studio.",
      bgImage: "/artevic.png",
      projectScope: [
        {
          header: "BRAND NARRATIVE",
          desc: "Artevic's tagline — 'One Agency, Infinite Editing Possibilities' — demanded a website that felt limitless yet focused. We developed a dark-themed, cinematic interface that lets the work speak for itself, with portfolio video embeds, client testimonials, and a clear service breakdown that converts browsers into clients.",
          image: artevicService1,
        },
        {
          header: "SERVICE ARCHITECTURE",
          desc: "Structured four distinct service pillars — Talking Head Video, Podcast Video & Reels, SaaS Promo, and Social Media Strategy — each with dedicated portfolio links. The information architecture guides visitors from awareness to action, with clear CTAs at every decision point.",
          image: artevicService4,
          images: [artevicService2, artevicService3],
        },
        {
          header: "CLIENT TRUST SYSTEM",
          desc: "Featured testimonials from high-profile clients like Daymond John and Biohack Yourself, with real quotes and verified results. The social proof section is strategically placed after the service showcase to close the trust gap at the exact moment of consideration.",
        },
        {
          header: "CONVERSION FUNNEL",
          desc: "Integrated tiered pricing plans, Calendly scheduling for free consultations, and WhatsApp direct messaging for instant response. The multi-channel approach ensures no potential client falls through the cracks, regardless of their preferred communication method.",
        },
      ],
      gallery: [artevicGal1, artevicGal2, artevicGal3],
      summaryStatement:
        "ARTEVIC MEDIA'S WEBSITE IS A TESTAMENT TO THE POWER OF LETTING WORK SPEAK FOR ITSELF. WE DESIGNED A PLATFORM WHERE CINEMATIC QUALITY MEETS STRATEGIC CONVERSION — EVERY SCROLL TELLS A STORY, EVERY SECTION BUILDS TRUST, AND EVERY CTA DRIVES ACTION. FROM DAYMOND JOHN TO EMERGING CREATORS, ARTEVIC'S DIGITAL HOME REFLECTS THE CALIBER OF BRANDS THEY SERVE.",
    },
    metadata: {
      title: "ARTEVIC MEDIA — CINEMATIC CONTENT STUDIO",
      description:
        "A premium agency website for a video editing studio serving global brands including Daymond John and Biohack Yourself.",
      keywords: [
        "Video editing agency",
        "Content studio website",
        "Creative agency design",
        "Octarnal Case Study",
      ],
      ogImage: "/openg.jpg",
    },
  },
];
